/**
 * The sync engine. One data path, and only one.
 *
 * Every change that should reach the server — an edit, a lesson result, a
 * preference, and the records a learner chose to adopt when they first signed in
 * — goes through the same durable outbox. There is no second transport, because
 * a second transport is a second set of retry, idempotency and conflict rules,
 * and the two only have to disagree once.
 *
 * The order is always **pull, then push**. Pushing first would send a change
 * based on a revision the server has already moved past, turning a routine
 * update into a conflict the learner has to answer.
 *
 * Durability, precisely:
 *
 *   An operation is written to IndexedDB *before* any network attempt, keeps its
 *   `operationId` across every retry and app restart, and is deleted only after
 *   a settled acknowledgement. A dropped response is indistinguishable from a
 *   dropped request, so the only safe question is "did this id land?" — which is
 *   exactly what the server's idempotency table answers.
 *
 * Atomicity, precisely — and this is a limitation worth naming rather than
 * glossing: a pulled page is applied record by record and the cursor is advanced
 * only once the whole page has been applied. That is not one transaction across
 * every store, which IndexedDB cannot give us here. What it does guarantee is
 * that a crash mid-page replays that page rather than losing it, because the
 * cursor never moved and every apply is keyed by record id and idempotent.
 */

import { ApiClient, ApiError } from '../api/client';
import type { SyncStores } from './stores';
import {
  payloadsEqual,
  readLocalRecord,
  writeRemote,
  type LocalContext,
} from './records';
import {
  newOperationId,
  type PulledRecord,
  type ConflictRecord,
  type OutboxOperation,
  type PullPage,
  type PushResult,
  type SyncKind,
  type SyncStatus,
} from './types';

/** How many operations go in one push. The server rejects larger batches. */
const PUSH_BATCH = 50;
/** Pulled records per page. */
const PULL_LIMIT = 100;
/** Attempts after which an operation is reported rather than retried silently. */
const ATTEMPTS_BEFORE_ATTENTION = 5;

export interface SyncEngineOptions {
  accountId: string;
  api: ApiClient;
  stores: SyncStores;
  local: LocalContext;
  onChange?: (snapshot: SyncSnapshot) => void;
  /** Debounce before a queued write is sent. Local persistence never waits. */
  debounceMs?: number;
}

export interface SyncSnapshot {
  status: SyncStatus;
  pending: number;
  conflicts: ConflictRecord[];
  lastSyncedAt: number | null;
  /** Set when something needs a person: the first unresolved reason. */
  attention: string | null;
}

export class SyncEngine {
  private readonly accountId: string;
  private readonly api: ApiClient;
  private readonly stores: SyncStores;
  private readonly local: LocalContext;
  private readonly onChange?: (snapshot: SyncSnapshot) => void;
  private readonly debounceMs: number;

  private inFlight: Promise<void> | null = null;
  /**
   * Set by `stop()`, and never unset.
   *
   * An engine belongs to one account. When the learner signs out or a different
   * account signs in, this one is discarded — and it has to become genuinely
   * inert, not merely un-scheduled. Clearing the debounce timer alone left a
   * discarded engine able to run again from any in-flight continuation, pushing
   * one account's queue through a client that now belongs to another.
   */
  private stopped = false;
  private queuedRun = false;
  /** Set when the session ended. Nothing is sent until authentication returns. */
  private paused = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private snapshot: SyncSnapshot = {
    status: 'saved-local',
    pending: 0,
    conflicts: [],
    lastSyncedAt: null,
    attention: null,
  };

  constructor(options: SyncEngineOptions) {
    this.accountId = options.accountId;
    this.api = options.api;
    this.stores = options.stores;
    this.local = options.local;
    this.onChange = options.onChange;
    this.debounceMs = options.debounceMs ?? 1200;
  }

  current(): SyncSnapshot {
    return this.snapshot;
  }

  /**
   * True when the server is known to hold this record.
   *
   * Set by a pull that saw it. Adoption uses this as its compatibility check:
   * a bound record either already matched — which is exactly what the earlier
   * direct-copy build left behind — or has diverged and already has a conflict
   * recorded. Neither wants a fresh operation.
   */
  async isBound(kind: SyncKind, recordId: string): Promise<boolean> {
    return (await this.stores.binding(kind, recordId)) !== null;
  }

  /** What is still queued. For status, and for tests that assert durability. */
  async pendingOperations(): Promise<OutboxOperation[]> {
    return this.stores.pending();
  }

  /**
   * Queue a local change for the server.
   *
   * Returns as soon as the operation is durable. Pencil and autosave writes call
   * this and must never wait on a network request — the local write has already
   * happened by the time we are here.
   */
  async enqueue(input: {
    kind: SyncKind;
    recordId: string;
    payload: Record<string, unknown>;
    title?: string;
    feature?: string;
    schemaVersion?: number;
    deleted?: boolean;
  }): Promise<void> {
    const binding = await this.stores.binding(input.kind, input.recordId);
    const baseRevision = binding?.revision ?? null;

    const operation: OutboxOperation = {
      operationId: newOperationId(),
      accountId: this.accountId,
      kind: input.kind,
      recordId: input.recordId,
      payload: input.payload,
      title: input.title ?? '',
      feature: input.feature ?? '',
      schemaVersion: input.schemaVersion ?? 1,
      baseRevision,
      deleted: input.deleted ?? false,
      queuedAt: Date.now(),
      attempts: 0,
    };

    await this.stores.enqueue(operation);
    await this.publish();
    this.schedule();
  }

  /** Run soon, coalescing a burst of edits into one round trip. */
  schedule(): void {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, this.debounceMs);
  }

  /** The session came back. Resume where the queue left off. */
  resume(): void {
    if (this.stopped) return;
    this.paused = false;
    void this.run();
  }

  /** Retire this engine for good. The queue itself is durable and untouched. */
  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /**
   * One full cycle: pull, reconcile if this account has never run, then push.
   *
   * Re-entrant calls coalesce into a single trailing run rather than overlapping,
   * because two concurrent cycles would race on the cursor.
   */
  async run(): Promise<void> {
    // Await the cycle already in flight rather than returning immediately. A
    // caller that says `await engine.run()` means "and let it finish"; returning
    // early made that silently untrue and is exactly the kind of thing that
    // turns into a flaky test today and a lost edit later.
    if (this.stopped) return;
    if (this.inFlight) {
      this.queuedRun = true;
      await this.inFlight;
      return;
    }
    if (this.paused) return;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await this.publish('offline');
      return;
    }

    this.inFlight = this.cycle();
    try {
      await this.inFlight;
    } finally {
      this.inFlight = null;
    }
    if (this.queuedRun && !this.stopped) {
      this.queuedRun = false;
      await this.run();
    }
  }

  private async cycle(): Promise<void> {
    try {
      await this.publish('syncing');
      await this.pull();
      // Retired mid-cycle: stop before sending anything. `stop()` can land
      // during any of the awaits above, and an engine that has been handed off
      // — signed out, or replaced by another account's — must not go on to push
      // through a client that is no longer its own.
      if (this.stopped) return;

      // Reconciliation is not a separate pass: the pull above already bound or
      // conflicted every record the server holds, which is exactly what
      // reconciliation means. The flag records that it has happened at least
      // once for this account.
      await this.stores.setMeta({ reconciled: true });

      await this.push();
      await this.stores.setMeta({ lastPushedAt: Date.now() });
      await this.publish();
    } catch (error) {
      await this.handleFailure(error);
    }
  }

  // ------------------------------------------------------------------- pull

  private async pull(): Promise<void> {
    for (let page = 0; page < 200; page++) {
      if (this.stopped) return;
      const meta = await this.stores.meta();
      const query = new URLSearchParams({ limit: String(PULL_LIMIT) });
      if (meta.cursor) query.set('since', meta.cursor);

      const result = await this.api.get<PullPage>(`/sync/pull?${query.toString()}`);

      for (const record of result.records) await this.absorb(record);

      // Only now, with the whole page applied. A crash above replays this page;
      // a crash here replays it too. Neither loses it.
      await this.stores.setMeta({ cursor: result.cursor, lastPulledAt: Date.now() });

      if (!result.has_more) return;
    }
  }

  /**
   * Take one pulled record, without ever losing an unsent local change.
   *
   * This is the decision the whole engine exists to get right, and there are
   * exactly four cases:
   *
   *   **Nothing local.** Write it. Nothing can be lost.
   *
   *   **Payloads already equal.** Bind the revisions and write nothing. This is
   *   the case that keeps learners whole who used the earlier direct-copy
   *   adoption: their documents are already on the server under the same UUIDs,
   *   so they are recognised rather than duplicated or overwritten.
   *
   *   **Local unchanged since the two sides last agreed.** A safe fast-forward:
   *   this device has nothing to lose, so the server's version wins.
   *
   *   **Local changed too.** Both are real work. Keep both as a conflict copy
   *   and let the learner choose. Raw ink is never merged and never silently
   *   replaced — there is no correct way to interleave two people's
   *   handwriting, so the engine does not invent one.
   *
   * The third and fourth cases are only distinguishable because the binding
   * stores this device's revision at the moment of agreement as well as the
   * server's.
   */
  private async absorb(record: PulledRecord): Promise<void> {
    const binding = await this.stores.binding(record.kind, record.record_id);
    const local = await readLocalRecord(this.local, record.kind, record.record_id);

    if (!local) {
      const localRevision = await writeRemote(this.local, record);
      await this.stores.bind(record.kind, record.record_id, record.revision, localRevision);
      return;
    }

    // A tombstone before anything else. Its payload is usually the record's last
    // known content, so an equality check would decide there was nothing to do
    // and the delete would never arrive — a document deleted on one iPad would
    // quietly reappear on the other.
    if (record.deleted) {
      if (!local.deleted) {
        const localRevision = await writeRemote(this.local, record);
        await this.stores.bind(record.kind, record.record_id, record.revision, localRevision);
      } else {
        await this.stores.bind(record.kind, record.record_id, record.revision, local.revision);
      }
      return;
    }

    if (payloadsEqual(local.payload, record.payload)) {
      await this.stores.bind(record.kind, record.record_id, record.revision, local.revision);
      return;
    }

    // Lesson progress is monotonic and merges without a decision; see
    // `mergeProgress`. It never reaches the conflict path.
    const mergeable = record.kind === 'lesson_progress' || record.kind === 'practice_session';
    const untouchedSinceAgreement = binding !== null && binding.localRevision === local.revision;

    if (mergeable || untouchedSinceAgreement) {
      const localRevision = await writeRemote(this.local, record);
      await this.stores.bind(record.kind, record.record_id, record.revision, localRevision);
      return;
    }

    // Both sides changed. Preserve both.
    await this.stores.addConflict({
      id: newOperationId(),
      accountId: this.accountId,
      kind: record.kind,
      recordId: record.record_id,
      title: local.title || record.title || record.record_id,
      localPayload: local.payload,
      remotePayload: record.payload,
      remoteRevision: record.revision,
      detectedAt: Date.now(),
      resolved: false,
    });
    // Bound to the server's revision so a later resolution is sent against the
    // right base, but the local record itself is left exactly as the learner
    // left it.
    await this.stores.bind(record.kind, record.record_id, record.revision, local.revision);
  }

  // ------------------------------------------------------------------- push

  private async push(): Promise<void> {
    if (this.stopped) return;
    const pending = await this.stores.pending();
    if (pending.length === 0) return;

    for (let index = 0; index < pending.length; index += PUSH_BATCH) {
      if (this.stopped) return;
      const batch = pending.slice(index, index + PUSH_BATCH);
      const body = {
        operations: batch.map((operation) => ({
          operation_id: operation.operationId,
          kind: operation.kind,
          record_id: operation.recordId,
          payload: operation.payload,
          title: operation.title,
          feature: operation.feature,
          schema_version: operation.schemaVersion,
          base_revision: operation.baseRevision,
          deleted: operation.deleted,
        })),
      };

      let results: PushResult[];
      try {
        const response = await this.api.post<{ results: PushResult[] }>('/sync/push', body);
        results = response.results;
      } catch (error) {
        // The batch did not settle. Every operation keeps its id and its place
        // in the queue, so the retry asks the server the same question again.
        for (const operation of batch) {
          await this.stores.markAttempt(operation.operationId, describe(error));
        }
        throw error;
      }

      const byId = new Map(results.map((result) => [result.operation_id, result]));
      for (const operation of batch) {
        const result = byId.get(operation.operationId);
        if (!result) {
          // The server said nothing about it. Leave it queued rather than
          // assuming either outcome.
          await this.stores.markAttempt(operation.operationId, 'No result returned.');
          continue;
        }
        await this.settle(operation, result);
      }
    }
  }

  private async settle(operation: OutboxOperation, result: PushResult): Promise<void> {
    if (result.status === 'applied') {
      if (typeof result.revision === 'number') {
        // Re-read the local revision: the learner may have written again while
        // this operation was in flight, and binding a stale number would make
        // the next pull look like a fast-forward and quietly discard that edit.
        const local = await readLocalRecord(this.local, operation.kind, operation.recordId);
        await this.stores.bind(
          operation.kind,
          operation.recordId,
          result.revision,
          local?.revision ?? null,
        );
      }
      await this.stores.settle(operation.operationId);
      return;
    }

    if (result.status === 'conflict') {
      await this.recordConflict(operation, result);
      // Settled as far as the queue is concerned: replaying it unchanged would
      // conflict again forever. The learner's answer produces a new operation.
      await this.stores.settle(operation.operationId);
      return;
    }

    // Rejected: a payload the server will never accept. Retrying cannot help, so
    // it is surfaced rather than looped.
    await this.stores.settle(operation.operationId);
    await this.stores.addConflict({
      id: newOperationId(),
      accountId: this.accountId,
      kind: operation.kind,
      recordId: operation.recordId,
      title: operation.title || operation.recordId,
      localPayload: operation.payload,
      remotePayload: {},
      remoteRevision: 0,
      detectedAt: Date.now(),
      resolved: false,
    });
  }

  /**
   * Keep both versions. Neither is discarded and neither is merged.
   *
   * There is no correct way to interleave two people's handwriting, so the
   * engine does not try. The local payload and the server's are both stored, and
   * the learner chooses which to continue from.
   */
  private async recordConflict(operation: OutboxOperation, result: PushResult): Promise<void> {
    if (typeof result.revision === 'number') {
      const local = await readLocalRecord(this.local, operation.kind, operation.recordId);
      await this.stores.bind(
        operation.kind,
        operation.recordId,
        result.revision,
        local?.revision ?? null,
      );
    }
    await this.stores.addConflict({
      id: newOperationId(),
      accountId: this.accountId,
      kind: operation.kind,
      recordId: operation.recordId,
      title: operation.title || operation.recordId,
      localPayload: operation.payload,
      remotePayload: result.server_payload ?? {},
      remoteRevision: result.revision ?? 0,
      detectedAt: Date.now(),
      resolved: false,
    });
  }

  /**
   * Continue from one side of a conflict.
   *
   * Choosing the local version enqueues it against the server's current revision
   * — the change is re-sent, not forced. Choosing the server's version simply
   * accepts what was already pulled.
   */
  async resolveConflict(conflictId: string, keep: 'local' | 'remote'): Promise<void> {
    const conflict = (await this.stores.conflicts()).find((row) => row.id === conflictId);
    if (!conflict) return;

    if (keep === 'local') {
      await this.enqueue({
        kind: conflict.kind,
        recordId: conflict.recordId,
        payload: conflict.localPayload,
        title: conflict.title,
      });
    }
    await this.stores.resolveConflict(conflictId);
    await this.publish();
  }

  // ----------------------------------------------------------------- status

  private async handleFailure(error: unknown): Promise<void> {
    if (error instanceof ApiError && error.kind === 'unauthenticated') {
      // Pause rather than fail. The queue is intact, the work is on the iPad,
      // and the session banner already tells the learner what happened —
      // repeating it as a sync problem would be noise, not information.
      this.paused = true;
      await this.publish('saved-local');
      return;
    }
    if (error instanceof ApiError && (error.kind === 'offline' || error.kind === 'timeout')) {
      await this.publish('offline');
      return;
    }
    await this.publish('needs-attention', describe(error));
  }

  private async publish(force?: SyncStatus, attention?: string): Promise<void> {
    const pending = await this.stores.pending();
    const conflicts = await this.stores.conflicts();
    const meta = await this.stores.meta();

    const stuck = pending.find((operation) => operation.attempts >= ATTEMPTS_BEFORE_ATTENTION);

    let status: SyncStatus;
    if (conflicts.length > 0) status = 'needs-attention';
    else if (force) status = force;
    else if (stuck) status = 'needs-attention';
    else if (pending.length > 0) status = 'syncing';
    else if (meta.lastPushedAt) status = 'synced';
    else status = 'saved-local';

    this.snapshot = {
      status,
      pending: pending.length,
      conflicts,
      lastSyncedAt: meta.lastPushedAt,
      attention:
        attention ??
        (conflicts.length > 0
          ? `${conflicts.length} item${conflicts.length === 1 ? '' : 's'} need a decision`
          : (stuck?.lastError ?? null)),
    };
    this.onChange?.(this.snapshot);
  }
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}

export { payloadsEqual };
